/**
 * ChatMessage — renders a single message bubble (user or assistant).
 * Assistant messages support markdown-like formatting and inline charts.
 */
import InlineChart from './InlineChart'

/** Minimal markdown → JSX: bold, italic, code, line breaks. */
function renderMarkdown(text) {
  if (!text) return null

  const lines = text.split('\n')
  const elements = []

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]

    // Headers
    if (line.startsWith('### ')) {
      elements.push(<h4 key={i} className="font-semibold text-ink mt-3 mb-1">{line.slice(4)}</h4>)
      continue
    }
    if (line.startsWith('## ')) {
      elements.push(<h3 key={i} className="font-semibold text-ink mt-3 mb-1">{line.slice(3)}</h3>)
      continue
    }

    // Bullet points
    if (line.match(/^[\-\*]\s/)) {
      elements.push(
        <div key={i} className="flex gap-2 ml-2">
          <span className="text-ink-muted select-none">•</span>
          <span>{formatInline(line.slice(2))}</span>
        </div>
      )
      continue
    }

    // Empty lines as spacing
    if (!line.trim()) {
      elements.push(<div key={i} className="h-2" />)
      continue
    }

    // Regular paragraph
    elements.push(<p key={i} className="leading-relaxed">{formatInline(line)}</p>)
  }

  return elements
}

/** Inline formatting: **bold**, *italic*, `code`, numbers with commas */
function formatInline(text) {
  if (!text) return text

  const parts = []
  let remaining = text
  let keyIdx = 0

  // Process bold, italic, and code
  const regex = /(\*\*(.+?)\*\*)|(\*(.+?)\*)|(`(.+?)`)/g
  let match
  let lastIndex = 0

  while ((match = regex.exec(text)) !== null) {
    // Add text before match
    if (match.index > lastIndex) {
      parts.push(<span key={keyIdx++}>{text.slice(lastIndex, match.index)}</span>)
    }

    if (match[1]) {
      // Bold
      parts.push(<strong key={keyIdx++} className="font-semibold text-ink">{match[2]}</strong>)
    } else if (match[3]) {
      // Italic
      parts.push(<em key={keyIdx++}>{match[4]}</em>)
    } else if (match[5]) {
      // Code
      parts.push(
        <code key={keyIdx++} className="px-1.5 py-0.5 bg-surface-tertiary rounded text-xs font-mono text-brand">
          {match[6]}
        </code>
      )
    }

    lastIndex = match.index + match[0].length
  }

  if (lastIndex < text.length) {
    parts.push(<span key={keyIdx++}>{text.slice(lastIndex)}</span>)
  }

  return parts.length > 0 ? parts : text
}

export default function ChatMessage({ message, charts = [] }) {
  const isUser = message.role === 'user'

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
      <div
        className={`max-w-[85%] rounded-xl px-4 py-3 text-sm ${
          isUser
            ? 'bg-brand text-ink-onAccent rounded-br-sm'
            : 'bg-surface-tertiary text-ink border border-edge-subtle rounded-bl-sm'
        }`}
      >
        {isUser ? (
          <p className="leading-relaxed">{message.content}</p>
        ) : (
          <div className="space-y-1">
            {renderMarkdown(message.content)}
            {charts.map((chart, i) => (
              <InlineChart key={i} spec={chart} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
